const GIFT_RULES = [
  {
    // Any mattress -> 1 free duvet ('ПОДАРЪК Лятна олекотена завивка "Деметра" бяла', 150x200)
    id: 'mattress-free-duvet',
    triggerProductType: 'Mattresses',
    // Excluded because they already carry their own gift:
    excludeProductIds: [
      8427448467803, // Матрак iZone 7 от Sleepy (gets a free pillow instead)
    ],
    excludeVendors: [
      'ТЕД',
      'PARADISE',
      'НАНИ',
      'БЛЯН',
      'Don Almohadon',
      'Латекс ЕКОН',
      'РОСМАРИ',
      'TEMPUR',
      'ESSENCE', // all ТЕД mattresses
    ],
    fixedGiftVariantId: 53879786144091,
    matchByVariantTitle: false,
    // 'once' = 1 duvet per cart, no matter how many mattresses.
    // 'per-unit' = one duvet per mattress unit.
    giftQuantityMode: 'per-unit',
    giftQuantity: 1,
  },
];

const GIFT_PROPERTY_KEY = '_gift_for_product_id';
const giftProductsCache = {};

const themeRoutes = window.theme?.routes || {};
const CART_BASE_URL = themeRoutes.cart_url || '/cart';
const CART_ADD_URL = themeRoutes.cart_add_url || '/cart/add.js';
const CART_CHANGE_URL = themeRoutes.cart_change_url || '/cart/change.js';
const CART_JSON_URL = CART_BASE_URL.endsWith('.js') ? CART_BASE_URL : `${CART_BASE_URL}.js`;

const isAnyGiftItem = (item) => {
  return Boolean(item?.properties && typeof item.properties[GIFT_PROPERTY_KEY] !== 'undefined');
};

// Rules keyed by product id keep using that id, so gifts already sitting in a
// customer's cart stay matched after this file changes.
const getRuleId = (rule) => rule.id || String(rule.triggerProductId);

const isGiftForRule = (item, ruleId) => {
  return item?.properties?.[GIFT_PROPERTY_KEY] === ruleId;
};

// The gift products are published at 0 лв, so a customer can also add them
// manually from their product page (the cart line links to it). Those lines
// carry no gift property, but must still be managed by the rule — otherwise
// they stay in the cart for free after the trigger product is removed.
const isGiftVariantItem = (rule, item) => {
  if (rule.fixedGiftVariantId && item?.variant_id === rule.fixedGiftVariantId) return true;
  if (rule.giftProductHandle && item?.handle === rule.giftProductHandle) return true;
  return false;
};

const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isTriggerItem = (rule, item) => {
  if (isAnyGiftItem(item)) return false;
  if (!rule.triggerProductId && !rule.triggerProductType) return false;

  if (rule.triggerProductId && item.product_id !== rule.triggerProductId) return false;

  if (rule.triggerProductType && normalize(item.product_type) !== normalize(rule.triggerProductType)) {
    return false;
  }

  if (rule.excludeProductIds?.includes(item.product_id)) return false;

  if (rule.excludeVendors?.some((vendor) => normalize(vendor) === normalize(item.vendor))) return false;

  return true;
};

const fetchGiftProductByHandle = async (handle) => {
  if (giftProductsCache[handle]) return giftProductsCache[handle];

  try {
    const response = await fetch(`/products/${handle}.js`, {
      method: 'GET',
      headers: {Accept: 'application/json'},
    });

    if (!response.ok) {
      console.error('[GIFT] Gift product fetch failed:', handle, response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    giftProductsCache[handle] = data;
    return data;
  } catch (error) {
    console.error('[GIFT] Error fetching gift product JSON for', handle, error);
    return null;
  }
};

const findMatchingGiftVariantForHandle = async (giftHandle, triggerVariantTitle) => {
  const giftProduct = await fetchGiftProductByHandle(giftHandle);
  if (!giftProduct || !Array.isArray(giftProduct.variants)) return null;

  const matchingVariant = giftProduct.variants.find(
    (variant) => variant.title === triggerVariantTitle || variant.option1 === triggerVariantTitle,
  );

  return matchingVariant ? matchingVariant.id : null;
};

const getCart = async () => {
  const response = await fetch(CART_JSON_URL, {
    method: 'GET',
    headers: {Accept: 'application/json'},
  });

  if (!response.ok) {
    throw new Error(`[GIFT] Failed to fetch cart: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

const addCartItem = async ({id, quantity, properties}) => {
  return fetch(CART_ADD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({id, quantity, properties}),
  });
};

const changeCartLine = async ({key, quantity}) => {
  return fetch(CART_CHANGE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({id: key, quantity}),
  });
};

const buildGiftOperations = async (cart) => {
  const items = cart?.items || [];
  const operations = [];

  for (const rule of GIFT_RULES) {
    const {giftProductHandle, matchByVariantTitle, fixedGiftVariantId} = rule;
    const ruleId = getRuleId(rule);

    const triggerItems = items.filter((item) => isTriggerItem(rule, item) && !isGiftVariantItem(rule, item));
    const existingGiftItems = items.filter((item) => isGiftForRule(item, ruleId) || isGiftVariantItem(rule, item));

    if (!triggerItems.length) {
      existingGiftItems.forEach((giftItem) => {
        operations.push({type: 'change', key: giftItem.key, quantity: 0});
      });
      continue;
    }

    const requiredByGiftVariantId = new Map();

    if (rule.giftQuantityMode === 'once') {
      // One gift for the whole cart, regardless of how many trigger items it holds.
      if (fixedGiftVariantId) {
        requiredByGiftVariantId.set(fixedGiftVariantId, rule.giftQuantity || 1);
      }
    } else {
      for (const triggerItem of triggerItems) {
        const triggerQty = triggerItem.quantity || 0;
        if (triggerQty <= 0) continue;

        let giftVariantId = null;

        if (matchByVariantTitle) {
          giftVariantId = await findMatchingGiftVariantForHandle(giftProductHandle, triggerItem.variant_title);
        } else {
          giftVariantId = fixedGiftVariantId;
        }

        if (!giftVariantId) continue;

        const nextQty = (requiredByGiftVariantId.get(giftVariantId) || 0) + triggerQty;
        requiredByGiftVariantId.set(giftVariantId, nextQty);
      }
    }

    const existingByGiftVariantId = new Map();
    existingGiftItems.forEach((giftItem) => {
      const list = existingByGiftVariantId.get(giftItem.variant_id) || [];
      list.push(giftItem);
      existingByGiftVariantId.set(giftItem.variant_id, list);
    });

    requiredByGiftVariantId.forEach((requiredQty, giftVariantId) => {
      const existingLines = existingByGiftVariantId.get(giftVariantId) || [];

      if (!existingLines.length) {
        operations.push({
          type: 'add',
          id: giftVariantId,
          quantity: requiredQty,
          properties: {
            [GIFT_PROPERTY_KEY]: ruleId,
          },
        });
        return;
      }

      const [primaryLine, ...extraLines] = existingLines;
      const existingTotalQty = existingLines.reduce((sum, line) => sum + (line.quantity || 0), 0);

      if (existingTotalQty !== requiredQty) {
        operations.push({type: 'change', key: primaryLine.key, quantity: requiredQty});
      }

      extraLines.forEach((line) => {
        operations.push({type: 'change', key: line.key, quantity: 0});
      });

      existingByGiftVariantId.delete(giftVariantId);
    });

    existingByGiftVariantId.forEach((lines) => {
      lines.forEach((line) => {
        operations.push({type: 'change', key: line.key, quantity: 0});
      });
    });
  }

  return operations;
};

const applyOperations = async (operations) => {
  if (!operations.length) return false;

  for (const operation of operations) {
    let response;

    if (operation.type === 'add') {
      response = await addCartItem(operation);
    } else {
      response = await changeCartLine(operation);
    }

    if (!response.ok) {
      console.error('[GIFT] Cart operation failed:', operation, response.status, response.statusText);
    }
  }

  return true;
};

let isSyncInProgress = false;
let hasQueuedSync = false;

const runGiftSync = async () => {
  if (isSyncInProgress) {
    hasQueuedSync = true;
    return;
  }

  isSyncInProgress = true;

  try {
    const cart = await getCart();
    const operations = await buildGiftOperations(cart);
    const changed = await applyOperations(operations);

    if (changed) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
    }
  } catch (error) {
    console.error('[GIFT] Failed to sync gift rules', error);
  } finally {
    isSyncInProgress = false;

    if (hasQueuedSync) {
      hasQueuedSync = false;
      runGiftSync();
    }
  }
};

const initGiftWithProduct = () => {
  runGiftSync();
  document.addEventListener('theme:cart:change', runGiftSync);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftWithProduct);
} else {
  initGiftWithProduct();
}
